// buildAuthoringExport — Plan B3 Task 2 (cogo.template.v1 authoring lane).
//
// Composes `@cogo/template`'s `buildTemplate` (the SOLE authoring gate —
// verificationEvidence check -> validateSlotDecls -> computeStructureHash ->
// self assertRoundTrip; see gates.ts) with the THREE download artifacts a
// human author needs in order to hand-carry a new template into the repo:
//
//  - templateJson: the built `TemplatePackage` itself, serialized
//    `JSON.stringify(pkg, null, 2) + '\n'` — BYTE-IDENTICAL format to
//    `apps/cogo-studio/scripts/generate-template-registry.mjs`'s own write,
//    so the downloaded file can become `<templateId>.template.json` in
//    `src/features/templates/registry/` verbatim.
//
//  - filesJson: a per-file sha256 manifest, `{path, sha256}[]` sorted by
//    path ascending — same SHAPE as the registry's `<templateId>.files.json`.
//    ⚠️ HASHING BASIS HONESTLY DIFFERS FROM THE REPO GENERATOR:
//    `generate-template-registry.mjs` hashes the RAW ON-DISK BYTES of each
//    `.json` file (`createHash('sha256').update(fileBytes)` over whatever
//    literal whitespace/key-order that file happens to have on disk). This
//    module runs in the browser, where there is no "file on disk" to hash —
//    the only bytes available are the in-memory `Json` value at
//    `bundle.files[path]`, so it hashes `canonicalJson(value)`'s UTF-8 bytes
//    instead (sorted keys, no incidental whitespace) via WebCrypto. The two
//    manifests are therefore NOT guaranteed to produce the same sha256 for
//    the same logical file content — only when the on-disk bytes already
//    happen to equal the canonical form. Treat this panel's `filesJson` as a
//    reference/diff aid, not a drop-in replacement for the repo generator's
//    manifest: regenerate `<templateId>.files.json` via
//    `generate-template-registry.mjs` (or equivalent) at commit time.
//
//  - bundleJson: the bundle's own `files` map, pretty-printed — the raw
//    serialized project, for operators to diff/inspect/re-derive from.
//
// Registration itself (writing `registry.ts` + the two files under
// `registry/`, passing Ajv + the drift-lock gate in
// `template-registry.gate.test.ts`) stays entirely repo-gated — this
// function's output is hand-carry INPUT to that gate, never a bypass of it.
// The panel surfaces that honestly via a static procedure note; see
// `TemplateAuthoringPanel.tsx`.

import type { AppBundle, Json, SlotDecl, TemplatePackage, TypedFailure } from '@cogo/template'
import { buildTemplate, canonicalJson } from '@cogo/template'
import type { SerializedBundle } from './serialize-project-to-bundle'

export interface AuthoringExport {
  templateJson: string
  filesJson: string
  bundleJson: string
}

export interface BuildAuthoringExportInput {
  bundle: SerializedBundle
  /** The human-confirmed subset (possibly constraint-edited) of proposeSlots' output. */
  confirmedSlots: SlotDecl[]
  templateId: string
  version: string
  kind: TemplatePackage['kind']
  projectRef: string
  /**
   * B3 Task-2 review fix (2026-07-25): was hardcoded to `'allowed'`, which
   * made `maskBundle` throw (`design file "design-system.json" not found`,
   * surfaced as `slot-address-missing`) for every server project — see
   * `serialize-project-to-bundle.ts`'s header comment: `LoadedApp.
   * designSystem` is never populated by `openServerProject`'s boot path, so
   * `bundle.files['design-system.json']` was reliably absent there. Now a
   * REQUIRED caller-supplied parameter: the caller (TemplateAuthoringPanel)
   * knows whether ANY design source (raw or derived) actually made it into
   * `bundle.files['design-system.json']` and must pass `'allowed'` only
   * when it did, `'pinned'` otherwise. `'pinned'` skips the design-file
   * mask/rebind step entirely (see canonical.ts's `maskBundle`), so it is
   * always safe to pass when the bundle has no design-system.json.
   */
  designRebinding: 'allowed' | 'pinned'
}

export type BuildAuthoringExportResult =
  | { ok: true; export: AuthoringExport }
  | { ok: false; failures: TypedFailure[] }

// Byte-identical to packages/cogo-schema/v4/template.cogo.schema.json's own
// `templateId` pattern — the authority; this is a client-side PRE-check so a
// malformed id fails fast, before ever calling buildTemplate.
const TEMPLATE_ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/

// `crypto.subtle.digest` wants a `BufferSource`; passing the `ArrayBuffer`
// directly (rather than the `Uint8Array` view) sidesteps a TS lib.dom.d.ts
// generic-`ArrayBufferLike`-vs-`ArrayBuffer` overload mismatch under this
// package's tsconfig — same pattern already established in
// `features/templates/instantiate-service.ts`'s `sha256OfBytes`.
async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes.buffer as ArrayBuffer)
  let out = ''
  for (const b of new Uint8Array(digest)) out += b.toString(16).padStart(2, '0')
  return out
}

export async function buildAuthoringExport(input: BuildAuthoringExportInput): Promise<BuildAuthoringExportResult> {
  if (!TEMPLATE_ID_RE.test(input.templateId)) {
    return {
      ok: false,
      failures: [
        {
          code: 'template-id-invalid',
          message: `templateId "${input.templateId}" does not match ^[a-z0-9][a-z0-9-]{0,63}$`,
        },
      ],
    }
  }
  if (input.version.trim().length === 0) {
    return {
      ok: false,
      failures: [{ code: 'template-version-invalid', message: 'version must be a non-empty string' }],
    }
  }

  // SerializedBundle.files is Record<string, unknown> (Task 1's contract —
  // the studio project source it's built from isn't statically Json-typed);
  // @cogo/template's AppBundle wants Record<string, Json>. The cast is safe
  // here: every value ultimately originates from parsed JSON (block/manifest
  // state persisted as JSON), and buildTemplate's own canonicalJson calls
  // will throw a TypedFailure-wrapped TypeError (see gates.ts's
  // `caughtAsFailure`) if that ever turns out false for a given value.
  const bundle = input.bundle as unknown as AppBundle

  const built = await buildTemplate({
    bundle,
    slots: input.confirmedSlots,
    templateId: input.templateId,
    version: input.version,
    kind: input.kind,
    projectRef: input.projectRef,
    verificationEvidence: [`authored-in-studio:${input.projectRef}`],
    designRebinding: input.designRebinding,
  })
  if (!built.ok) return { ok: false, failures: built.failures }

  const templateJson = JSON.stringify(built.pkg, null, 2) + '\n'

  const manifest: { path: string; sha256: string }[] = []
  for (const path of Object.keys(bundle.files).sort()) {
    const bytes = new TextEncoder().encode(canonicalJson(bundle.files[path] as Json))
    manifest.push({ path, sha256: `sha256:${await sha256Hex(bytes)}` })
  }
  const filesJson = JSON.stringify(manifest, null, 2) + '\n'

  const bundleJson = JSON.stringify(bundle.files, null, 2) + '\n'

  return { ok: true, export: { templateJson, filesJson, bundleJson } }
}
